import { useEffect, useMemo, useState } from 'react';
import { useUserTimezone } from './useUserTimezone';
import {
  DASHBOARD_REFRESH_UNITS,
  clearTimeSelection,
  loadTimeSelection,
  resolveDashboardTimeWindow,
  saveTimeSelection
} from '../utils/dashboardTimeRange';

export function useDashboardTimeRange(fromState, { autoRefresh = true } = {}) {
  const { timezone } = useUserTimezone();
  const [selection, setSelection] = useState(() => loadTimeSelection(fromState));
  const [tick, setTick] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const resolved = useMemo(
    () => resolveDashboardTimeWindow(selection, timezone),
    [selection, timezone, tick]
  );

  useEffect(() => {
    saveTimeSelection(selection);
  }, [selection]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const amount = Number(selection.refreshAmount);
    if (!Number.isFinite(amount) || amount < 1) return undefined;
    const unit = DASHBOARD_REFRESH_UNITS.find((item) => item.id === selection.refreshUnit)
      || DASHBOARD_REFRESH_UNITS[1];
    const timer = setInterval(() => {
      setTick((n) => n + 1);
      if ((selection.timeMode || 'absolute') === 'absolute') {
        setRefreshTrigger((n) => n + 1);
      }
    }, amount * unit.ms);
    return () => clearInterval(timer);
  }, [autoRefresh, selection.refreshAmount, selection.refreshUnit, selection.timeMode]);

  const updateSelection = (patch) => {
    setSelection((prev) => ({ ...prev, ...patch }));
    setTick((n) => n + 1);
  };

  const clear = () => {
    setSelection(clearTimeSelection());
    setTick((n) => n + 1);
    setRefreshTrigger((n) => n + 1);
  };

  const refreshNow = () => {
    setTick((n) => n + 1);
    setRefreshTrigger((n) => n + 1);
  };

  return {
    selection,
    updateSelection,
    startTime: resolved.startTime,
    endTime: resolved.endTime,
    refreshTrigger,
    clear,
    refreshNow
  };
}
